/**
 * Avatar Crop Dialog
 * LinkedIn-style profile picture crop & reposition modal.
 * Uses react-easy-crop with a round crop area, zoom slider, and drag to reposition.
 */

import type { Area } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { useCallback, useState } from "react";

import { Check, Loader2, Minus, Plus, RotateCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { uploadFile } from "@/services/upload.service";

interface AvatarCropDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	imageSrc: string | null;
	onUpload: (url: string) => void;
}

/**
 * Crop the image on a canvas and return a File blob.
 */
async function getCroppedFile(
	imageSrc: string,
	pixelCrop: Area,
	rotation = 0,
): Promise<File> {
	const image = await createImage(imageSrc);
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	if (!ctx) throw new Error("Canvas not supported");

	const rotRad = (rotation * Math.PI) / 180;

	// Calculate bounding box of the rotated image
	const bBoxWidth =
		Math.abs(Math.cos(rotRad) * image.width) +
		Math.abs(Math.sin(rotRad) * image.height);
	const bBoxHeight =
		Math.abs(Math.sin(rotRad) * image.width) +
		Math.abs(Math.cos(rotRad) * image.height);

	// Set canvas size to the bounding box
	canvas.width = bBoxWidth;
	canvas.height = bBoxHeight;

	// Translate to center, rotate, then draw
	ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
	ctx.rotate(rotRad);
	ctx.translate(-image.width / 2, -image.height / 2);
	ctx.drawImage(image, 0, 0);

	// Create a second canvas for the cropped output
	const outputCanvas = document.createElement("canvas");
	const outputCtx = outputCanvas.getContext("2d");
	if (!outputCtx) throw new Error("Canvas not supported");

	// Force output to 400x400 for consistent profile picture size
	const outputSize = 400;
	outputCanvas.width = outputSize;
	outputCanvas.height = outputSize;

	outputCtx.drawImage(
		canvas,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		outputSize,
		outputSize,
	);

	// Convert to blob → File
	return new Promise((resolve, reject) => {
		outputCanvas.toBlob(
			(blob) => {
				if (!blob) return reject(new Error("Failed to create blob"));
				const file = new File([blob], "profile-picture.webp", {
					type: "image/webp",
				});
				resolve(file);
			},
			"image/webp",
			0.92,
		);
	});
}

function createImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.addEventListener("load", () => resolve(img));
		img.addEventListener("error", reject);
		img.crossOrigin = "anonymous";
		img.src = url;
	});
}

export function AvatarCropDialog({
	open,
	onOpenChange,
	imageSrc,
	onUpload,
}: AvatarCropDialogProps) {
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const onCropComplete = useCallback(
		(_croppedArea: Area, croppedAreaPx: Area) => {
			setCroppedAreaPixels(croppedAreaPx);
		},
		[],
	);

	const handleSave = async () => {
		if (!imageSrc || !croppedAreaPixels) return;

		setIsUploading(true);
		setError(null);

		try {
			// Crop client-side first
			const croppedFile = await getCroppedFile(
				imageSrc,
				croppedAreaPixels,
				rotation,
			);

			// Upload the cropped file
			const result = await uploadFile(croppedFile, { folder: "profile-pictures" });

			if (result.success && result.url) {
				onUpload(result.url);
				handleClose();
			} else {
				setError(result.error || "Upload failed");
			}
		} catch {
			setError("Failed to process image");
		} finally {
			setIsUploading(false);
		}
	};

	const handleClose = () => {
		if (!isUploading) {
			setCrop({ x: 0, y: 0 });
			setZoom(1);
			setRotation(0);
			setCroppedAreaPixels(null);
			setError(null);
			onOpenChange(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md" onInteractOutside={(e) => isUploading && e.preventDefault()}>
				<DialogHeader>
					<DialogTitle>Edit profile photo</DialogTitle>
				</DialogHeader>

				{/* Cropper area */}
				<div className="relative mx-auto h-[300px] w-[300px] overflow-hidden rounded-lg bg-black/90">
					{imageSrc && (
						<Cropper
							image={imageSrc}
							crop={crop}
							zoom={zoom}
							rotation={rotation}
							aspect={1}
							cropShape="round"
							showGrid={false}
							onCropChange={setCrop}
							onZoomChange={setZoom}
							onRotationChange={setRotation}
							onCropComplete={onCropComplete}
							style={{
								containerStyle: { borderRadius: "0.35rem" },
								cropAreaStyle: {
									border: "2px solid color-mix(in srgb, var(--color-dashboard-paper) 50%, transparent)",
								},
							}}
						/>
					)}
				</div>

				{/* Controls */}
				<div className="space-y-4 px-2">
					{/* Zoom */}
					<div className="flex items-center gap-3">
						<Minus className="h-4 w-4 shrink-0 text-muted-foreground" />
						<Slider
							value={[zoom]}
							min={1}
							max={3}
							step={0.01}
							onValueChange={([v]) => setZoom(v)}
							className="flex-1"
						/>
						<Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
						<span className="w-10 text-right text-xs text-muted-foreground">
							{Math.round(zoom * 100)}%
						</span>
					</div>

					{/* Rotation */}
					<div className="flex items-center gap-3">
						<RotateCw className="h-4 w-4 shrink-0 text-muted-foreground" />
						<Slider
							value={[rotation]}
							min={0}
							max={360}
							step={1}
							onValueChange={([v]) => setRotation(v)}
							className="flex-1"
						/>
						<span className="w-10 text-right text-xs text-muted-foreground">
							{rotation}°
						</span>
					</div>
				</div>

				{error && <p className="text-sm text-destructive text-center">{error}</p>}

				{/* Actions */}
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={isUploading}
					>
						<X className="mr-1 h-4 w-4" />
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={isUploading}>
						{isUploading ? (
							<>
								<Loader2 className="mr-1 h-4 w-4 animate-spin" />
								Saving...
							</>
						) : (
							<>
								<Check className="mr-1 h-4 w-4" />
								Apply
							</>
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
